import { ROUTES } from 'routes'

/**
 * Router state a link to Help carries, so the page can name where it came from.
 *
 * Help is reached from THREE places — a Profile row, a certificate's options menu, and
 * the certificates page's account menu — so its back-link cannot be a static prop the way
 * Notifications' "Profile" is. The sender knows the answer and the destination does not,
 * which is the same shape as the platform's referrer rule (working-conventions §42): the
 * link carries `{ backTo, backLabel }` and the receiving header renders it.
 *
 * Carrying the ROUTE as well as the label (rather than `navigate(-1)`) is what keeps the
 * back-slide working: `usePushPage` injects `slideBack` for a route, so the parent enters
 * from the left and the stack visibly unwinds. A caller-supplied function owns its own
 * navigation and gets no such flag, so `navigate(-1)` would play the exit and then hard
 * cut — motion followed by a jump, which reads as a flash rather than as going back.
 */
export interface HelpNavState {
  backTo: string
  backLabel: string
}

export const helpNavState = (backLabel: string, backTo: string): HelpNavState => ({
  backLabel,
  backTo,
})

/**
 * Where Help returns to when it was opened without state.
 *
 * Unreachable from the three wired entry points, all of which pass a label. It exists
 * because the alternative to a fallback is a page with no way out: `/help` is not in
 * `showsPrimaryNav`, so there is no bottom bar underneath it to escape by, and this app
 * runs on a `MemoryRouter` with no browser chrome. Certificates is the app's home and is
 * the right place to land from anywhere.
 */
export const HELP_DEFAULT_BACK: HelpNavState = {
  backTo: ROUTES.CERTIFICATES,
  backLabel: 'My certificates',
}

/**
 * Route to one article, built from `ROUTES` rather than typed as a literal.
 *
 * Three call sites need it (the Help list, a `TermSheet`'s "Learn more", an inline
 * article-to-article link in the markdown), and `CertificatesMenu` records at length what
 * hand-built paths cost in this app. The failure there was worse than drift — a
 * `window.location`-derived path under a `MemoryRouter` in a shadow root is the HOST
 * page's address — but the drift argument stands on its own with three copies.
 */
export const helpArticlePath = (slug: string) => ROUTES.HELP_ARTICLE.replace(':slug', slug)
